import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useSessionContext } from '@/context/SessionContextProvider';
import { useToast } from '@/hooks/use-toast';

export interface ChannelUserBinding {
  id: string;
  channel_id: string;
  user_id: string;
  network_id: string;
  created_at: string;
}

/**
 * Vínculos canal <-> usuário. Quando um canal não tem nenhum vínculo, ele é
 * "aberto" (todos os usuários com acesso ao Inbox podem ver as conversas).
 * Quando há vínculos, apenas os usuários listados podem ver.
 */
export const useChannelUsers = (channelId?: string) => {
  const { networkId } = useSessionContext();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: ['crm-channel-users', channelId],
    queryFn: async (): Promise<ChannelUserBinding[]> => {
      if (!channelId) return [];
      const { data, error } = await supabase
        .from('crm_channel_users' as any)
        .select('*')
        .eq('channel_id', channelId);
      if (error) throw error;
      return (data || []) as unknown as ChannelUserBinding[];
    },
    enabled: !!channelId,
  });

  const setUsers = useMutation({
    mutationFn: async (userIds: string[]) => {
      if (!channelId || !networkId) throw new Error('Canal ou rede inválidos');

      // Apaga todos e recria — simples e seguro pois há unique(channel_id,user_id)
      const { data: deleted, error: delErr } = await supabase
        .from('crm_channel_users' as any)
        .delete()
        .eq('channel_id', channelId)
        .select('id');
      if (delErr) throw delErr;
      void deleted;

      if (userIds.length > 0) {
        const rows = userIds.map(uid => ({
          channel_id: channelId,
          user_id: uid,
          network_id: networkId,
        }));
        const { error: insErr } = await supabase
          .from('crm_channel_users' as any)
          .insert(rows as any);
        if (insErr) throw insErr;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['crm-channel-users', channelId] });
      queryClient.invalidateQueries({ queryKey: ['crm-channel-users-all'] });
      queryClient.invalidateQueries({ queryKey: ['crm-team'] });
      queryClient.invalidateQueries({ queryKey: ['crm-conversations'] });
      toast({ title: 'Acesso ao canal atualizado' });
    },
    onError: (e: any) => {
      toast({ title: 'Erro ao salvar acesso', description: e.message, variant: 'destructive' });
    },
  });

  const userIds = useMemo(() => (query.data || []).map(b => b.user_id), [query.data]);

  return {
    bindings: query.data || [],
    userIds,
    isLoading: query.isLoading,
    setUsers,
  };
};

/**
 * Busca todos os vínculos de canais da rede de uma vez (para filtro do Inbox).
 */
export const useAllChannelUserBindings = () => {
  const { networkId } = useSessionContext();
  return useQuery({
    queryKey: ['crm-channel-users-all', networkId],
    queryFn: async (): Promise<ChannelUserBinding[]> => {
      if (!networkId) return [];
      const { data, error } = await supabase
        .from('crm_channel_users' as any)
        .select('*')
        .eq('network_id', networkId);
      if (error) throw error;
      return (data || []) as unknown as ChannelUserBinding[];
    },
    enabled: !!networkId,
  });
};

/**
 * Lista usuários ativos da rede (para o multiselect).
 */
/**
 * Lista usuários ativos da rede que tem acesso ao módulo CRM
 * (para o multiselect de acesso a canal).
 * Considera usuário com acesso se: role NETWORK_ADMIN, features NULL (acesso total),
 * ou array features contém 'CRM'.
 */
export const useNetworkUsers = () => {
  const { networkId } = useSessionContext();
  return useQuery({
    queryKey: ['network-users-crm-list', networkId],
    queryFn: async () => {
      if (!networkId) return [];
      const { data: roles, error } = await supabase
        .from('user_network_roles')
        .select('user_id, role, features')
        .eq('network_id', networkId)
        .eq('is_active', true);
      if (error) throw error;

      // Agrupa por user_id e determina acesso ao CRM
      const accessByUser = new Map<string, boolean>();
      for (const r of roles || []) {
        const hasCrm =
          r.role === 'NETWORK_ADMIN' ||
          r.features === null ||
          (Array.isArray(r.features) && (r.features as string[]).includes('CRM'));
        if (hasCrm) accessByUser.set(r.user_id, true);
        else if (!accessByUser.has(r.user_id)) accessByUser.set(r.user_id, false);
      }
      const ids = [...accessByUser.entries()].filter(([, ok]) => ok).map(([id]) => id);
      if (ids.length === 0) return [];

      const { data: profiles, error: pErr } = await supabase
        .from('profiles')
        .select('id, name, nickname, avatar_url')
        .in('id', ids);
      if (pErr) throw pErr;
      return (profiles || [])
        .map(p => {
          const display = (p.nickname?.trim() || p.name || 'Sem nome');
          return { id: p.id, name: display, fullName: p.name || '', avatarUrl: p.avatar_url };
        })
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    enabled: !!networkId,
  });
};
